import {
  AccessField,
  Binary,
  Call,
  Convert,
  Group,
  Identifier,
  Negate,
  Num,
  type ExpressionVisitor,
  type Expression,
  CalendarValue,
  SimpleEvalError,
} from './ast.ts';
import { type CalendarType, UnitConversionError, UnitsTracker } from './units.ts';
import { type ExpressionValidResultTypes, SimpleExpressionEvaluator } from './evaluator.ts';

export type TypeCheckResultBase =
  | { type: 'number'; units: UnitsTracker }
  | { type: 'calendarValue'; calendarType: CalendarType }
  | { type: 'string' | 'boolean' | 'function' };
export type TypeCheckResultWithError = TypeCheckResultBase | { type: 'error'; error: string };

// Num replaces a lone dimensionless unit (%, ppm, Scalar) with a unitless fraction whenever one is constructed, so every
// number is typed with its units normalized the same way:
function numberType(units: UnitsTracker): TypeCheckResultBase {
  return { type: 'number', units: units.convertToScalarIfPercentage(1) === false ? units : UnitsTracker.unitless() };
}

export class ExpressionTypeChecker implements ExpressionVisitor<TypeCheckResultWithError> {
  public readonly expression: Expression;
  public readonly variables: Map<string, TypeCheckResultBase>;

  constructor(expression: Expression, variables: Map<string, TypeCheckResultBase>) {
    this.expression = expression;
    this.variables = variables;
  }

  typeCheck(): TypeCheckResultWithError {
    try {
      return this.expression.accept(this);
    } catch (e: any) {
      return { type: 'error', error: e.message };
    }
  }

  visitNum(node: Num): TypeCheckResultWithError {
    return { type: 'number', units: node.units };
  }

  visitStr(): TypeCheckResultWithError {
    return { type: 'string' };
  }

  visitBool(): TypeCheckResultWithError {
    return { type: 'boolean' };
  }

  visitCalendarValue(node: CalendarValue): TypeCheckResultWithError {
    return { type: 'calendarValue', calendarType: node.value.calendarType };
  }

  visitIdentifier(node: Identifier): TypeCheckResultWithError {
    const val = this.variables.get(node.nameWithAnnotation);
    if (!val) {
      throw new Error(`Could not find identifier: ${node.nameWithAnnotation}`);
    }

    return val.type === 'number' ? numberType(val.units) : val;
  }

  visitAccessField(_node: AccessField): TypeCheckResultWithError {
    // TODO: 9. Formulas - This is a decent argument for removing the access operator, as it is thus far unused.
    throw new Error(`Cannot typecheck object access.`);
  }

  visitNegate(node: Negate): TypeCheckResultWithError {
    const innerNode = node.expr.accept(this);
    if (innerNode.type !== 'number') {
      throw new Error(`Cannot negate non-number type: ${innerNode.type}`);
    }

    return innerNode;
  }

  visitCall(node: Call): TypeCheckResultWithError {
    if (node.callee instanceof Identifier) {
      const funcName = node.callee.nameWithAnnotation;
      throw new Error(`Unexpected call to unknown function: ${funcName}`);
    } else {
      const func = node.callee.accept(this);
      if (func.type !== 'function') {
        throw new Error(`Expected function, got ${func}`);
      }

      // TODO: 9. Formulas - We'll need to change how we refer to functions in order to support this, but thus far
      //  they are only implemented in parsing, no production formulas contain function calls:
      throw new Error(`Cannot typecheck function call results.`);
    }
  }

  visitGroup(node: Group): TypeCheckResultWithError {
    return node.expr.accept(this);
  }

  visitConvert(node: Convert): TypeCheckResultWithError {
    const innerNode = node.expr.accept(this);
    if (innerNode.type === 'number') {
      return numberType(node.units);
    } else if (innerNode.type === 'calendarValue') {
      const calendarType = node.units.toCalendarType();
      if (calendarType === null) {
        throw new Error(
          `Cannot convert calendar value to: ${node.units.toString()} (${node.units.toDimensionString()})`,
        );
      }

      return { type: 'calendarValue', calendarType: calendarType };
    } else {
      throw new Error(`Cannot convert non-number type: ${innerNode.type}`);
    }
  }

  visitBinary(node: Binary): TypeCheckResultWithError {
    const left = node.left.accept(this);
    const right = node.right.accept(this);

    if (
      node.operator === '=' ||
      node.operator === '!=' ||
      node.operator === '<' ||
      node.operator === '>' ||
      node.operator === '<=' ||
      node.operator === '>='
    ) {
      if (
        (left.type === 'number' && right.type === 'number') ||
        (left.type === 'string' && right.type === 'string') ||
        (left.type === 'boolean' && right.type === 'boolean') ||
        (left.type === 'calendarValue' && right.type === 'calendarValue')
      ) {
        if (left.type === 'number' && right.type === 'number') {
          if (!left.units.canAddOrSubtract(right.units)) {
            throw new Error(`Cannot compare numbers of different units (${left.units} vs. ${right.units}).`);
          }
        }

        if (left.type === 'calendarValue' && right.type === 'calendarValue') {
          if (left.calendarType !== right.calendarType) {
            throw new Error(
              `Cannot compare calendar values of different types (${left.calendarType} vs. ${right.calendarType}).`,
            );
          }
        }

        return { type: 'boolean' };
      } else {
        throw new Error(`Cannot compare mismatched types: ${left.type} ${node.operator} ${right.type}`);
      }
    }

    if (left.type === 'string' || right.type === 'string') {
      if (node.operator === '+') {
        return { type: 'string' };
      } else {
        throw new Error(`Cannot use operator '${node.operator}' on strings`);
      }
    }

    if (left.type !== 'number') {
      throw new Error(`Cannot use ${node.operator} operator on non-number type: ${left.type}`);
    }

    if (right.type !== 'number') {
      throw new Error(`Cannot use ${node.operator} operator on non-number type: ${right.type}`);
    }

    if (node.operator === '+' || node.operator === '-') {
      if (left.units.canAddOrSubtract(right.units)) {
        return { type: 'number', units: left.units };
      } else {
        throw new Error(`Cannot use operator '${node.operator}' on mismatched units ${left.units} and ${right.units}`);
      }
    } else if (node.operator === '*') {
      return numberType(left.units.multiply(right.units));
    } else if (node.operator === '/') {
      return numberType(left.units.divide(right.units));
    } else if (node.operator === '^') {
      let powerNode: ExpressionValidResultTypes;

      if (node.right instanceof Num) {
        powerNode = node.right;
      } else {
        // Handle more complex cases like grouping (2^(x)) or arithmetic (2^(3+4)):
        powerNode = new SimpleExpressionEvaluator(node.right, new Map()).evaluate();
        if (powerNode instanceof SimpleEvalError) {
          // Most likely cause of error is something like 2^x, but it's also possible they had some other error like
          // trying to exponentiate a non-number:
          throw new Error(
            `Cannot type-check dynamic exponent number as we cannot know the units to produce (x^2 or x^3 or ...) (inner error: ${powerNode.message})`,
          );
        }
      }

      if (!(powerNode instanceof Num)) {
        throw new Error(`Cannot exponentiate to a non-number power: ${powerNode.toString()}`);
      }

      const units = left.units.power(powerNode.units, powerNode.value);
      if (units instanceof UnitConversionError) {
        throw new Error(`Cannot exponentiate due to a unit conversion error: ${units.message}`);
      }

      return numberType(units);
    } else {
      const unexpected: never = node.operator;
      throw new Error(`Encountered unexpected binary operator: ${unexpected}`);
    }
  }
}
